import { useCallback, useEffect, useMemo } from 'react'
import { useFormik, FormikProvider } from 'formik'
import { DateTime } from 'luxon'

import {
  createSingleAnnualTaxDetail,
  CREATE_SINGLE_ANNUAL_TAX_DETAIL_KEY,
  AnnualTaxDetail,
  updateSingleAnnualTaxDetail,
  UPDATE_SINGLE_ANNUAL_TAX_DETAIL_KEY,
  postUpdateCurrentTaxYear,
  FETCH_UPDATE_CURRENT_ANNUAL_TAX_DETAIL_KEY,
} from '../annualTaxDetails.slice'
import { getFetchError, getIsFetching } from '@/reducers/fetch'
import { useReselector } from '@/utils/sharedHooks'
import {
  FormikDateInput,
  FormikInput,
  getFieldName,
} from '@/componentsV1/BaseComponents'
import { getCurrentUser, getIsAdministrator } from '@/selectors/user.selectors'
import { DATE_FORMATS_LUXON, formatISOFromUTC } from '@/utils/dateHelpers'
import { useAppDispatch } from '@/utils/typeHelpers'
import {
  AnnualTaxDateMapping,
  AnnualTaxDateType,
} from '../annualTaxDateTypes.slice'
import { Dialog, Button, Alert, List } from '@/ui'
import { fetchAllRolesIfNeeded } from '@/actions/admin/roleActions'

interface Props {
  details?: AnnualTaxDetail
  dateTypes?: AnnualTaxDateType[]
  onCancel: () => void
}

const inputToStartIso = (val: string | undefined) =>
  val
    ? DateTime.fromFormat(val, DATE_FORMATS_LUXON.INPUT)
        .toUTC()
        .startOf('day')
        .toISO() || ''
    : ''

const AnnualTaxDetailsForm = ({ onCancel, dateTypes, details }: Props) => {
  const dispatch = useAppDispatch()

  const hasCreateErrored = useReselector(
    getFetchError,
    CREATE_SINGLE_ANNUAL_TAX_DETAIL_KEY
  )
  const hasUpdateErrored = useReselector(
    getFetchError,
    UPDATE_SINGLE_ANNUAL_TAX_DETAIL_KEY + details?.taxYear
  )

  const isUpdatingTaxYear = useReselector(
    getIsFetching,
    FETCH_UPDATE_CURRENT_ANNUAL_TAX_DETAIL_KEY
  )

  const currentUser = useReselector(getCurrentUser)

  const isAdministrator = useReselector(getIsAdministrator)

  useEffect(() => {
    dispatch(fetchAllRolesIfNeeded())
  }, [dispatch])

  const formattedDates = useMemo(() => {
    const sortedDateTypes = dateTypes?.sort((a, b) => {
      return a.id.localeCompare(b.displayName)
    })
    return sortedDateTypes?.reduce((acc, curr) => {
      acc[curr.id] = formatISOFromUTC(details?.[curr.id])
      return acc
    }, {} as AnnualTaxDateMapping)
  }, [dateTypes, details])

  const formik = useFormik({
    initialValues: {
      taxYear: details?.taxYear || '',
      ...formattedDates,
    },
    onSubmit: async (values) => {
      const { taxYear, ...dates } = values
      const taxDates = Object.entries(dates).map(([key, value]) => {
        return { id: key, value: inputToStartIso(value) }
      })
      const dataContents = {
        taxDates,
        modifiedBy: currentUser?.id,
      }

      let data
      if (details) {
        data = await updateSingleAnnualTaxDetail(
          details.taxYear,
          dataContents
        )(dispatch)
      } else {
        data = await createSingleAnnualTaxDetail({ taxYear, ...dataContents })(
          dispatch
        )
      }

      if (data) {
        onCancel()
      }
    },
  })

  const { submitForm, isSubmitting, values: _values } = formik

  const error = hasCreateErrored || hasUpdateErrored

  const dateInputListItems = useMemo(
    () =>
      dateTypes
        ?.sort((a, b) => {
          const dateA = details?.[a.id] || null
          const dateB = details?.[b.id] || null
          if (!dateA || !dateB) {
            return 0
          }
          return DateTime.fromISO(dateA)
            .diff(DateTime.fromISO(dateB))
            .toMillis()
        })
        .map((dt) => (
          <>
            <List.Item key={`dateInputListItems-${dt.id}`}>
              <FormikDateInput
                dateFormat={DATE_FORMATS_LUXON.INPUT}
                name={getFieldName<typeof _values>(dt.id)}
                label={dt.displayName}
                description={`${dt.description}${dt.archivedAt ? ' (Archived)' : ''}`}
                required={!dt.archivedAt}
                disabled={Boolean(dt.archivedAt)}
              />
            </List.Item>
            <br />
          </>
        )),
    [dateTypes, details]
  )

  const updateCurrentYear = useCallback(async () => {
    if (!details?.taxYear) {
      return
    }
    const res = await dispatch(postUpdateCurrentTaxYear(details.taxYear))

    if (res) {
      onCancel()
    }
  }, [details?.taxYear, dispatch, onCancel])

  return (
    <FormikProvider value={formik}>
      <Dialog>
        {details?.status === 'future' && isAdministrator && (
          <Dialog.Trigger asChild>
            <Button loading={isUpdatingTaxYear}>Make Current</Button>
          </Dialog.Trigger>
        )}
        <Dialog.Content>
          <Dialog.Header>
            Set {details?.taxYear} as the current tax year
          </Dialog.Header>
          <Dialog.Description>
            This will set the current tax year as {details?.taxYear}, update the
            other details to have past years, create tax questionnaire filters,
            and create details, forms, and year end tax statuses for all users
            with annual tax permissions. It is IRREVERSIBLE, so make SURE that
            you want to do this
          </Dialog.Description>
          <Dialog.Footer>
            <Dialog.Close asChild>
              <Button variant="secondary">Cancel</Button>
            </Dialog.Close>
            <Button onClick={updateCurrentYear} variant="danger">
              Make Current
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog>

      <List>
        <List.Item>
          <FormikInput
            name={getFieldName<typeof _values>('taxYear')}
            label="Tax Year"
            description="e.g. 2023"
            required
          />
        </List.Item>
        <br />
        {dateInputListItems}
      </List>

      {error && <Alert variant="error">{error.message}</Alert>}
      <Button onClick={onCancel} variant="secondary">
        Cancel
      </Button>
      <Button
        disabled={isSubmitting}
        loading={isSubmitting}
        onClick={submitForm}
      >
        Save
      </Button>
    </FormikProvider>
  )
}

export default AnnualTaxDetailsForm
